/**
 * La tabla de negocios de la portada: cifras del periodo + foto de la agenda.
 * Spec 042: grupos con raya; Conversaciones es título, no un total.
 */
import { Link, useNavigate } from 'react-router'

import type { ContadoresVista } from '../components/Contadores'
import { useVerComo } from '../ver-como'

export interface ConversionVista {
    numerador: number
    denominador: number
    tasa: number | null
}

export interface AgendaVista {
    trabajadores: number | null
    servicios: number | null
    reservadas: number | null
}

export interface FilaNegocio {
    accountId: number
    name: string
    contadores: ContadoresVista
    conversion: ConversionVista
    estadoAlta?: string
    agenda?: AgendaVista
    /** Quién hay ahora en la cola. `null` = no se pudo leer. */
    enCola?: number | null
}

export function pct(c: ConversionVista): string {
    return c.tasa === null ? '—' : `${Math.round(c.tasa * 100)}%`
}

function cifra(n: number | null | undefined): string {
    return n === null || n === undefined ? '—' : String(n)
}

export const AYUDA_PORCENTAJE_RESERVA =
    'De quienes vinieron a reservar, cuántas acabaron en cita. Las abiertas y las que solo consultaron no entran.'

const CELDA = 'px-3 py-3 tabular-nums md:text-center'
const CABEZA = 'px-3 py-2 text-center font-medium'

export function TablaNegocios({
    negocios,
    tono = 'normal',
    demo = false,
}: {
    negocios: FilaNegocio[]
    tono?: 'normal' | 'demo'
    demo?: boolean
}) {
    const borde = tono === 'demo' ? 'border-amber-500/30' : 'border-neutral-800'
    const cabeza = tono === 'demo' ? 'bg-amber-500/5' : 'bg-neutral-900/60'
    const filaHover = tono === 'demo' ? 'hover:bg-amber-500/5' : 'hover:bg-neutral-900/40'
    const sep = tono === 'demo' ? 'border-amber-500/10' : 'border-neutral-900'
    const raya = tono === 'demo' ? 'md:border-l md:border-amber-500/25' : 'md:border-l md:border-neutral-700'

    return (
        <div className={`overflow-x-auto rounded-lg border ${borde}`}>
            <table className="tabla-carta w-full text-xs">
                <thead className={`${cabeza} text-left text-neutral-500`}>
                    <tr>
                        <th rowSpan={2} className="px-3 py-2 font-medium align-middle">
                            Negocio
                        </th>
                        <th
                            rowSpan={2}
                            className={`${CABEZA} align-middle ${raya}`}
                            title="Profesionales en la agenda"
                        >
                            Trabajadores
                        </th>
                        <th rowSpan={2} className={`${CABEZA} align-middle`} title="Servicios en el catálogo">
                            Servicios
                        </th>
                        <th
                            rowSpan={2}
                            className={`${CABEZA} align-middle ${raya}`}
                            title="Citas que aún no han empezado. No es del periodo de conversaciones."
                        >
                            Reservas
                        </th>
                        <th colSpan={4} className={`${CABEZA} ${raya}`}>
                            Conversaciones
                        </th>
                        <th rowSpan={2} className={`${CABEZA} align-middle ${raya}`} title={AYUDA_PORCENTAJE_RESERVA}>
                            % reserva
                        </th>
                        <th rowSpan={2} className="px-3 py-2" />
                    </tr>
                    <tr>
                        <th className={`${CABEZA} ${raya}`} title="Conversaciones que siguen abiertas en este periodo">
                            Pendientes
                        </th>
                        <th className={CABEZA}>En cita</th>
                        <th className={CABEZA} title="Quién está ahora en la lista de espera. No es del periodo.">
                            En espera
                        </th>
                        <th
                            className={CABEZA}
                            title="Conversaciones de este periodo que acabaron anulando una cita"
                        >
                            Anuladas
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {negocios.map((n) => (
                        <tr key={n.accountId} className={`border-t ${sep} ${filaHover}`}>
                            <td className="px-3 py-3" data-label="Negocio">
                                <Link
                                    to={`/business/${n.accountId}`}
                                    className="font-medium text-neutral-100 hover:text-sky-400"
                                >
                                    {n.name}
                                </Link>
                                {demo && (
                                    <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-400/90">
                                        demo
                                    </span>
                                )}
                            </td>
                            <td
                                className={`${CELDA} text-neutral-300 ${raya}`}
                                data-label="Trabajadores"
                            >
                                {cifra(n.agenda?.trabajadores)}
                            </td>
                            <td className={`${CELDA} text-neutral-300`} data-label="Servicios">
                                {cifra(n.agenda?.servicios)}
                            </td>
                            <td className={`${CELDA} text-sky-300 ${raya}`} data-label="Reservas">
                                {cifra(n.agenda?.reservadas)}
                            </td>
                            <td className={`${CELDA} text-neutral-300 ${raya}`} data-label="Pendientes">
                                {n.contadores.enCurso}
                            </td>
                            <td className={`${CELDA} text-emerald-400`} data-label="En cita">
                                {n.contadores.cerradasBien}
                            </td>
                            <td className={`${CELDA} text-orange-300`} data-label="En espera">
                                <Link
                                    to={`/business/${n.accountId}/espera`}
                                    className="hover:text-orange-200"
                                    title="Ver la lista de espera"
                                >
                                    {cifra(n.enCola)}
                                </Link>
                            </td>
                            <td className={`${CELDA} text-amber-400`} data-label="Anuladas">
                                {n.contadores.canceladas}
                            </td>
                            <td className={`${CELDA} text-neutral-300 ${raya}`} data-label="% reserva">
                                {pct(n.conversion)}
                            </td>
                            <td className="px-3 py-3 text-right" data-label="">
                                <div className="flex flex-wrap items-center justify-end gap-3">
                                    <VerComoCliente accountId={n.accountId} nombre={n.name} />
                                    <Link
                                        to={`/business/${n.accountId}`}
                                        className="inline-flex min-h-11 items-center text-sky-400 hover:text-sky-300 md:min-h-0"
                                    >
                                        Entrar →
                                    </Link>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function VerComoCliente({ accountId, nombre }: { accountId: number; nombre: string }) {
    const { entrar } = useVerComo()
    const navigate = useNavigate()
    return (
        <button
            type="button"
            onClick={() => {
                entrar({ accountId, nombre })
                navigate(`/business/${accountId}`)
            }}
            className="inline-flex min-h-11 items-center text-neutral-400 hover:text-sky-300 md:min-h-0"
        >
            Ver como cliente
        </button>
    )
}
